import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE, VIEW, EDIT, DATE_FORMAT } from "const"
import {
    MtStudySession,
    MtStudySessionCreate,
} from "server/models"
import {
    StateType,
    useInput,
    replaceHistoryState,
    forceRerenderContent,
} from "client/utils"
import {
    StoreType,
    StudySessionValidation,

    SaveStudySession,

    setStudySessionEdit,
    setStudySessionReloadList,
    setStudySessionReloadView,
    setStudySessionReloadEdit,
    closeModal,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Textarea,
    Feedback,
    DatePicker,
    Empty,
    SelectDropdown,
    SelectDropdownItem,
    Button,
    SpinnerIcon,
} from "client/components"

interface EditStudySessionProps {
    session: MtStudySession
}

let isMounted: boolean

const Form = ({
    session,
}: EditStudySessionProps) => {
    const baseRoute = useSelector((state: StoreType) => state.studySession.baseRoute)
    const studyList = useSelector((state: StoreType) => state.study.studyList)
    const studyIds = useSelector((state: StoreType) => state.study.studyIds)
    const teamMemberList = useSelector((state: StoreType) => state.studyTeamMember.teamMemberList)
    const teamMemberListByStudy = useSelector((state: StoreType) => state.studyTeamMember.teamMemberListByStudy)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const projectSettings = useSelector((state: StoreType) => state.project.projectSettings)
    const accountSettings = useSelector((state: StoreType) => state.system.accountSettings)

    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const { sessionFields } = projectSettings.study.sessionFieldsAssigned ? projectSettings.study : accountSettings.study

    const [comment, setComment] = useInput(session.comment)
    const [startTime, setStartTime] = useInput(session.startTime)
    const [endTime, setEndTime] = useInput(session.endTime)

    const [date, setDate] = useState(session.date ? new Date(session.date) : null)
    const [studyId, setStudyId] = useState(session.studyId)
    const [facilitatorId, setFacilitatorId] = useState(session.facilitatorId)
    const [scribeId, setScribeId] = useState(session.scribeId)

    const [validation, setValidation] = useState<StudySessionValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        if (!session.id) {
            setDate(new Date())
            if (studyIds.length) setStudyId(studyIds[0])
        }
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            if (isMounted) dispatch(closeModal())
            return
        }

        const path = session.id
            ? `${baseRoute}/${VIEW}/${session.id}`
            : baseRoute

        forceRerenderContent(path, dispatch)
    }

    const onSave = (mode: number) => {
        if (startTime.length === 0 || endTime.length === 0) {
            setValidation({ startTime: lang.timeRequired })
            return
        }

        const startDate = new Date(date)
        const endDate = new Date(date)

        const startTimeParsed = startTime.split(":")
        const endTimeParsed = endTime.split(":")

        const startHours = parseInt(startTimeParsed[0], 10)
        const startMinutes = parseInt(startTimeParsed[1], 10)
        const endHours = parseInt(endTimeParsed[0], 10)
        const endMinutes = parseInt(endTimeParsed[1], 10)

        startDate.setHours(startHours, startMinutes)
        endDate.setHours(endHours, endMinutes)

        if (startDate >= endDate) {
            setValidation({ startTime: lang.startTimeLessEndTime })
            return
        }

        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newSessionData: MtStudySessionCreate = {
            projectId: project.id,
            studyId,
            date,
            startTime,
            endTime,
            facilitatorId,
            scribeId,
            comment,
        }

        SaveStudySession(session.id, newSessionData, lang)
        .then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)

                if (res.newSessionId) replaceHistoryState(`${baseRoute}/${EDIT}/${res.newSessionId}`)
            }
            if (res.validation) return

            dispatch(setStudySessionEdit({
                id: res.newSessionId || session.id,
                ...newSessionData,
            }))

            dispatch(setStudySessionReloadList(false))
            dispatch(setStudySessionReloadView(false))
            setTimeout(() => {
                dispatch(setStudySessionReloadList(true))
                dispatch(setStudySessionReloadView(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setStudySessionReloadEdit(false))
                setTimeout(() => {
                    dispatch(setStudySessionReloadEdit(true))
                }, 0)
            }
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.study}</Label>
                        <SelectDropdown title={studyList[studyId] ? studyList[studyId].name : <Empty />}>
                            {
                                studyIds.map((id) => studyList[id] && (
                                    <SelectDropdownItem
                                        key={id}
                                        onClick={() => {
                                            setStudyId(id)
                                            if (studyId !== id) {
                                                setFacilitatorId(null)
                                                setScribeId(null)
                                            }
                                        }}
                                    >
                                        {studyList[id].name}
                                    </SelectDropdownItem>
                                ))
                            }
                        </SelectDropdown>
                        <Feedback>{validation && validation.studyId}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.date}</Label>
                        <FormGroup>
                            <DatePicker
                                selected={date}
                                onChange={(newDate: Date) => {
                                    if (!newDate) setDate(date)
                                    else setDate(newDate)
                                }}
                                dateFormat={DATE_FORMAT}
                            />
                        </FormGroup>
                        <Feedback>{validation && validation.date}</Feedback>
                    </FormGroup>
                </Col>
                <Col xs={6}>
                    <FormGroup>
                        <Label>{lang.startTime}</Label>
                        <Input type="time" max={endTime} value={startTime} onChange={setStartTime} />
                        <Feedback>{validation && validation.startTime}</Feedback>
                    </FormGroup>
                </Col>
                <Col xs={6}>
                    <FormGroup>
                        <Label>{lang.endTime}</Label>
                        <Input type="time" min={startTime} value={endTime} onChange={setEndTime} />
                        <Feedback>{validation && validation.endTime}</Feedback>
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    {
                        sessionFields.facilitator && (
                            <FormGroup>
                                <Label>{lang.facilitator}</Label>
                                <SelectDropdown title={teamMemberList[facilitatorId] ? teamMemberList[facilitatorId].name : <Empty />}>
                                    {
                                        teamMemberListByStudy[studyId]
                                        && teamMemberListByStudy[studyId].map((id) => teamMemberList[id] && (
                                            <SelectDropdownItem key={id} onClick={() => setFacilitatorId(id)}>
                                                {teamMemberList[id].name}
                                            </SelectDropdownItem>
                                        ))
                                    }
                                </SelectDropdown>
                                <Feedback>{validation && validation.facilitatorId}</Feedback>
                            </FormGroup>
                        )
                    }
                    {
                        sessionFields.scribe && (
                            <FormGroup>
                                <Label>{lang.scribe}</Label>
                                <SelectDropdown title={teamMemberList[scribeId] ? teamMemberList[scribeId].name : <Empty />}>
                                    {
                                        teamMemberListByStudy[studyId]
                                        && teamMemberListByStudy[studyId].map((id) => teamMemberList[id] && (
                                            <SelectDropdownItem key={id} onClick={() => setScribeId(id)}>
                                                {teamMemberList[id].name}
                                            </SelectDropdownItem>
                                        ))
                                    }
                                </SelectDropdown>
                                <Feedback>{validation && validation.scribeId}</Feedback>
                            </FormGroup>
                        )
                    }
                    {
                        sessionFields.comment && (
                            <FormGroup>
                                <Label>{lang.comment}</Label>
                                <Textarea value={comment} onChange={setComment} />
                                <Feedback>{validation && validation.comment}</Feedback>
                            </FormGroup>
                        )
                    }
                </Col>
                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
